import { useEffect, useState } from 'react';

/**
 * Prompt de instalação PWA. Aparece quando o browser dispara
 * `beforeinstallprompt` (Chrome/Edge/Android). Esconde-se se o user
 * já instalou ou recusou. Persiste recusa em localStorage por 30 dias.
 *
 * iOS Safari não suporta o evento — fallback: detecta UA e mostra
 * dica "Toque em compartilhar → Adicionar à tela inicial" só na 1ª
 * sessão móvel pós-login.
 */

const DISMISS_KEY = 'dentalflow.pwa-install-dismissed';
const DISMISS_DAYS = 30;

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** Foi dispensado há menos de DISMISS_DAYS? Lê sempre do storage (o Chrome
 * re-dispara beforeinstallprompt várias vezes por sessão — precisamos checar
 * a cada vez, senão o banner reabre mesmo depois do X). */
function isDismissed(): boolean {
  try {
    const at = localStorage.getItem(DISMISS_KEY);
    if (!at) return false;
    return (Date.now() - Number(at)) / 86_400_000 < DISMISS_DAYS;
  } catch {
    return false;
  }
}

export function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(
    null,
  );
  const [iosTip, setIosTip] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isDismissed()) return;

    // App já instalado (modo standalone) — não mostra
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;
    if (isStandalone) return;

    const onBefore = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      // O Chrome pode re-disparar este evento várias vezes na mesma sessão.
      // Só (re)abre se o usuário NÃO dispensou — senão o banner volta mesmo
      // depois do X.
      if (isDismissed()) return;
      // Pequeno delay pra não atropelar primeira interação.
      setTimeout(() => {
        if (!isDismissed()) setOpen(true);
      }, 5_000);
    };
    window.addEventListener('beforeinstallprompt', onBefore);

    // Instalou pelo prompt nativo em outra aba/sessão → não insistir.
    const onInstalled = () => dismiss();
    window.addEventListener('appinstalled', onInstalled);

    // iOS detection — sem evento equivalente.
    const ua = window.navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
    if (isIOS) {
      setTimeout(() => {
        if (isDismissed()) return;
        setIosTip(true);
        setOpen(true);
      }, 8_000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBefore);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === 'accepted') {
      setOpen(false);
    } else {
      dismiss();
    }
  };

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* storage indisponível — ao menos fecha nesta sessão */
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 max-w-sm bg-white rounded-xl shadow-2xl border-2 border-teal-300 p-4 space-y-2"
      data-testid="install-prompt"
    >
      <div className="flex items-start gap-2">
        <span className="text-3xl" aria-hidden>
          📲
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-ink-800">
            Instale o Kevins Clínicas
          </p>
          <p className="text-xs text-ink-600 mt-0.5">
            {iosTip
              ? 'Toque em Compartilhar (⎋) e depois em "Adicionar à Tela Inicial".'
              : 'Acesso rápido em 1 clique, mesmo offline. Funciona como app.'}
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="text-ink-400 hover:text-ink-700 text-lg"
          aria-label="Fechar"
        >
          ×
        </button>
      </div>
      {!iosTip && (
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={dismiss}
            className="px-3 py-1.5 text-xs rounded text-ink-600 hover:bg-ink-100"
          >
            Depois
          </button>
          <button
            type="button"
            onClick={install}
            className="px-3 py-1.5 text-xs rounded-md bg-teal-600 text-white hover:bg-teal-700 font-medium"
          >
            Instalar
          </button>
        </div>
      )}
    </div>
  );
}
